import z from 'zod'
import { AnnatureForm } from './annature'
import { DocusignForm } from './docusign'
import { InternalPurchaserSchema } from './purchaser'

export const EsignFormTypes = ['agentAppointmentForm'] as const
export type EsignFormType = (typeof EsignFormTypes)[number]

export type SigningService = 'annature' | 'docusign'

export type EsignFormData = AnnatureForm | DocusignForm | ManualForm

/** @deprecated Use EsignFormData instead */
export type AnnatureOrManualForm = EsignFormData

export type EsignForm = EsignFormData

export interface EsignFormStructure {
    esign?: EsignFormData
    signingService: SigningService
}

export interface ManualForm {
    formStatus: 'manual upload'
    fileUrl: string
    fileName: string
}

export interface FormToPurchaserTable {
    [key: string]: unknown
    orgId: string
    formType: EsignFormType | 'generic'
    formId: string
    purchaserId: string
    service: SigningService
    userId?: string
    createdAt: string
    timeToDie: number
}

export const getFormName = (formType: EsignFormType) => {
    switch (formType) {
        case 'agentAppointmentForm':
            return 'Agent Appointment Form'
        default:
            return 'Unknown Form'
    }
}

// Generic Esign Document Schema
export interface GenericEsignDocument {
    formName: string
    form: AnnatureForm | DocusignForm
    signingService?: SigningService
}

export const getEsignFormFromPurchaser = (
    params: CoreFormTypeSchema & { purchaser: InternalPurchaserSchema },
): EsignFormData | undefined => {
    const { purchaser, formType, ...data } = params

    if (formType === 'generic') {
        return purchaser.genericEsignDocuments?.[(data as { formId: string }).formId]?.form
    } else {
        return purchaser[formType]?.esign as AnnatureOrManualForm | undefined
    }
}

export const getFormFromPurchaser = (
    purchaser: InternalPurchaserSchema,
    formType: EsignFormType,
): EsignFormData | undefined => {
    return purchaser[formType]?.esign as EsignFormData | undefined
}

/** Get envelope ID from any esign form type (handles Annature .id vs DocuSign .envelopeId) */
export const getEnvelopeId = (form: EsignFormData): string | undefined => {
    if ('formStatus' in form && form.formStatus === 'manual upload') return undefined
    if ('envelopeId' in form) return (form as DocusignForm).envelopeId
    if ('id' in form) return (form as AnnatureForm).id
    return undefined
}

export const CoreFormTypeSchema = z
    .object({
        formType: z.literal('generic'),
        formId: z.string(),
    })
    .or(
        z.object({
            formType: z.enum([...EsignFormTypes]),
        }),
    )

export type CoreFormTypeSchema = z.infer<typeof CoreFormTypeSchema>

type GetPurchaserS3KeyParams =
    | {
          orgId: string
          purchaserId: string
          formType: EsignFormType
      }
    | {
          orgId: string
          purchaserId: string
          formType: 'generic'
          formId: string
      }

export const getPurchaserS3Key = (params: GetPurchaserS3KeyParams): string => {
    if (params.formType === 'generic') {
        return `${params.orgId}/${params.purchaserId}/forms/generic/${params.formId}.pdf`
    }
    return `${params.orgId}/${params.purchaserId}/forms/${params.formType}.pdf`
}
